<script setup>
import ProductContentConfigurationForm from '@frontJs/components/forms/projectManagement/ai/ProductContentConfigurationForm.vue';
import ProductContentFieldTable from '@frontJs/components/tables/projectManagement/ai/ProductContentFieldTable.vue';
import ProductContentTable from '@frontJs/components/tables/projectManagement/ai/ProductContentTable.vue';
import TenantReportProductContentTable from '@frontJs/components/tables/projectManagement/ai/tenantReports/ProductContentTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import { Head } from '@inertiajs/vue3';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { h, onMounted, ref } from 'vue';

const {
    tenant,
    customFields,
    productAttributes,
    defaultContextAttributeCodes,
} = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    customFields: {
        type: Array,
        required: false,
        default: () => [],
    },
    productAttributes: {
        type: Array,
        required: false,
        default: () => [],
    },
    defaultContextAttributeCodes: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const loading = ref(true);

const data = ref({
    toDo: 0,
    inProgress: 0,
    toBeApproved: 0,
    approved: 0,
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.post(
            route('front.projectManagement.ai.postProductContentCount', {
                tenant: tenant.id,
            }),
            {
                status: 'toDo',
            },
        ),
        axios.post(
            route('front.projectManagement.ai.postProductContentCount', {
                tenant: tenant.id,
            }),
            {
                status: 'inProgress',
            },
        ),
        axios.post(
            route('front.projectManagement.ai.postProductContentCount', {
                tenant: tenant.id,
            }),
            {
                status: 'toBeApproved',
            },
        ),
        axios.post(
            route('front.projectManagement.ai.postProductContentCount', {
                tenant: tenant.id,
            }),
            {
                status: 'approved',
            },
        ),
    ])
        .then((responses) => {
            data.value.toDo = responses[0].data.count;
            data.value.inProgress = responses[1].data.count;
            data.value.toBeApproved = responses[2].data.count;
            data.value.approved = responses[3].data.count;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

onMounted(() => {
    if (tenant) {
        updateData();
    }
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>Contenus produits - IA - Gestion de projet - Datakepler</title>
    </Head>

    <PageTitle :tenant-name="tenant ? tenant.name : undefined">
        Gestion de projet - IA - Contenus produits
    </PageTitle>

    <TabLayout
        v-if="tenant"
        :tabs="{
            contents: {
                title: 'Contenus',
                items: {
                    toDo: {
                        title: 'À faire',
                        total: data.toDo,
                        content: h(ProductContentTable, {
                            tenant: tenant,
                            customFields: customFields,
                            productAttributes: productAttributes,
                            defaultContextAttributeCodes:
                                defaultContextAttributeCodes,
                            status: 'toDo',
                            callback: () => updateData(),
                        }),
                    },
                    inProgress: {
                        title: 'En cours',
                        total: data.inProgress,
                        content: h(ProductContentTable, {
                            tenant: tenant,
                            customFields: customFields,
                            productAttributes: productAttributes,
                            defaultContextAttributeCodes:
                                defaultContextAttributeCodes,
                            status: 'inProgress',
                            callback: () => updateData(),
                        }),
                    },
                    toBeApproved: {
                        title: 'À valider',
                        total: data.toBeApproved,
                        content: h(ProductContentTable, {
                            tenant: tenant,
                            customFields: customFields,
                            productAttributes: productAttributes,
                            defaultContextAttributeCodes:
                                defaultContextAttributeCodes,
                            status: 'toBeApproved',
                            callback: () => updateData(),
                        }),
                    },
                    approved: {
                        title: 'Validées',
                        total: data.approved,
                        content: h(ProductContentTable, {
                            tenant: tenant,
                            customFields: customFields,
                            productAttributes: productAttributes,
                            defaultContextAttributeCodes:
                                defaultContextAttributeCodes,
                            status: 'approved',
                            callback: () => updateData(),
                        }),
                    },
                },
            },
            customFields: {
                title: 'Champs personnalisés',
                content: h(ProductContentFieldTable, {
                    tenant: tenant,
                }),
            },
            configuration: {
                title: 'Configuration',
                content: h(ProductContentConfigurationForm, {
                    tenant: tenant,
                    productAttributes: productAttributes,
                    defaultContextAttributeCodes: defaultContextAttributeCodes,
                }),
            },
        }"
        :loading="loading"
    />

    <TenantReportProductContentTable v-else />
</template>
